import { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Modal as ReactModal } from 'reactstrap';
import i18n from 'src/utils/translate';
import { closeModal } from 'src/modules/shared/modal/actions';
import { CloseButton, HeaderButton, Header as StyledHeader, Title } from 'styles-js/modals';
import Button from 'src/components/buttons/button';
import Icon from 'src/components/icon';

type modalProps = {
  acceptButtonText?: string,
  acceptButtonDataTestId?: string,
  cancelButtonText?: string,
  children: ReactNode,
  customHeader?: boolean,
  isResponsive?: boolean,
  noClose?: boolean, // modal can't be dismissed by clicking away from it
  onAccept?: () => void,
  onCancel?: () => void,
  onClose?: () => void,
  title?: string,
}

export default function DefaultModal({
  acceptButtonText = '',
  acceptButtonDataTestId = '',
  cancelButtonText = '',
  children,
  customHeader = false,
  isResponsive = false,
  noClose = false,
  onAccept = () => {},
  onCancel = () => {},
  onClose = () => {},
  title,
}: modalProps) {
  const dispatch = useDispatch();

  const onAcceptClick = () => {
    dispatch(closeModal());
    onAccept();
  };

  const onCancelClick = () => {
    dispatch(closeModal());
    onCancel();
  };

  return (
    <Modal isResponsive={isResponsive} noClose={noClose}
      onClose={onClose}>
      {!customHeader && <Header title={title} onClose={onClose} />}
      <Body>
        {children}
      </Body>
      {(!!acceptButtonText || !!cancelButtonText) &&
        <Footer>
          {!!cancelButtonText && <Button type="link" onClick={onCancelClick}>{cancelButtonText}</Button>}
          {!!acceptButtonText &&
            <Button dataTestId={acceptButtonDataTestId} type="primary" onClick={onAcceptClick}>{acceptButtonText}</Button>
          }
        </Footer>
      }
    </Modal>
  );
}

type Modal = {
  children: ReactNode;
  isResponsive?: boolean;
  noClose?: boolean, // modal can't be dismissed by clicking away from it
  onClose?: () => void;
}

export const Modal = ({children, isResponsive = false, noClose = false, onClose = () => {}}: Modal) => {
  const dispatch = useDispatch();

  const onCloseClick = () => {
    if (noClose) return;
    dispatch(closeModal());
    onClose();
  };

  return (
    <StyledModal
      backdropClassName="app-modal__backdrop"
      className={isResponsive ? 'responsive' : 'fullscreen'}
      isOpen={true}
      toggle={onCloseClick}
    >
      {children}
    </StyledModal>
  );
};

type Header = {
  hasCloseButton?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  title?: string;
}

export const Header = ({
  hasCloseButton = true,
  onBack,
  onClose = () => {},
  title,
}: Header) => {
  const dispatch = useDispatch();

  const onCloseClick = () => {
    dispatch(closeModal());
    onClose();
  };

  return (
    <StyledHeader>
      {onBack &&
        <HeaderButton onClick={onBack}>
          <Icon icon={'left-open'} />{i18n.t('Back')}
        </HeaderButton>
      }
      {title && <Title>{title}</Title>}
      {hasCloseButton &&
        <CloseButton onClick={onCloseClick}>
          {i18n.t('Close')}<Icon icon={'cross-2'} />
        </CloseButton>
      }
    </StyledHeader>
  );
};

// Overwriting reactstrap modal component
const StyledModal = styled(ReactModal)`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: none;
  height: 100%;
  margin: auto;

  .modal-content {
    max-width: 560px;
    border-radius: 8px;
    border: none;
    overflow: hidden;
    max-height: 90vh;
    @media (max-height: 720px) {
      height: 100%;
      max-height: 100vh;
      border-radius: 0;
    }
  }

  &.responsive {
    // Workaround for IE11, couldn't fix display issue of the footer
    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      height: auto;
    }
    .modal-content {
      margin: auto 20px;
      @media (${props => props.theme.underScreenXxsmall}) {
        margin: auto 10px;
      }
    }
  }

  &.fullscreen {
    .modal-content {
      @media (max-width: ${props => props.theme.screenXsMin}) {
        height: 100%;
        max-height: 100vh;
        border-radius: 0;
      }
    }
  }
`;

// padding for modal footer
export const Body = styled.div`
  margin-bottom: 85px;
  padding: 40px;
  overflow-y: auto;

  @media (max-width: ${props => props.theme.screenXsMin}) {
    margin-bottom: 105px;
    padding: 20px 20px 30px;
  }
`;

export const Footer = styled.div`
  display: flex;
  padding: 0 40px 40px;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: ${props => props.theme.colorWhite};
  button {
    flex: 1;
  }

  @media (max-width: ${props => props.theme.screenXsMin}) {
    padding: 0 20px 30px;
    flex-direction: column-reverse;
    button {
      margin-right: 0;
    }
  }
`;
